import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { ElasticsearchHit } from '../types/chat';

export interface ChatFilters {
  textSearch: string;
  userSearch: string[];
  clusterSearch: string[];
  fqdnSearch: string[];
  messageTypeSearch: string[];
  dateRange: {
    from: Date;
    to: Date;
  };
}

/** The message a context tab is centred on (Discord-style "jump to message"). */
export interface ChatContextAnchor {
  hitId: string;
  /** Raw Elasticsearch timestamp of the anchor message. */
  timestamp: string;
  serverFqdn: string;
  user: string;
}

export interface ChatContextState {
  anchor: ChatContextAnchor;
  /** The anchor message and older ones, newest-first (as returned by ES). */
  beforeHits: ElasticsearchHit[];
  /** Messages newer than the anchor, oldest-first. */
  afterHits: ElasticsearchHit[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  initialLoaded: boolean;
}

// Define the shape of a single tab's state
export interface ChatTabState {
  id: string;
  name: string;
  filters: ChatFilters;
  hits: ElasticsearchHit[];
  totalHits: number;
  aggregations: {
    clusters: string[];
    messageTypes: string[];
  };
  highlightedUsers: { [username: string]: string }; // username -> color
  isLoading: boolean;
  /** When set, the tab shows messages around an anchor instead of filtered search results. */
  context?: ChatContextState;
}

// Define the context's value shape
interface ChatTabsContextType {
  tabs: ChatTabState[];
  activeTabId: string | null;
  addTab: (filters?: Partial<ChatFilters>, name?: string) => void;
  addContextTab: (anchor: ChatContextAnchor, name?: string) => void;
  removeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateFilters: (tabId: string, newFilters: Partial<ChatFilters>) => void;
  updateTabState: (tabId: string, newState: Partial<Omit<ChatTabState, 'id' | 'filters'>>) => void;
  getActiveTab: () => ChatTabState | undefined;
  updateTabName: (id: string, name: string) => void;
  toggleUserHighlight: (tabId: string, username: string) => void;
}

const ChatTabsContext = createContext<ChatTabsContextType | undefined>(undefined);

export const useChatTabs = () => {
  const context = useContext(ChatTabsContext);
  if (!context) {
    throw new Error('useChatTabs must be used within a ChatTabsProvider');
  }
  return context;
};

const getDefaultFilters = (): ChatFilters => ({
  textSearch: '',
  userSearch: [],
  clusterSearch: [],
  fqdnSearch: [],
  messageTypeSearch: [],
  dateRange: {
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  },
});

export const ChatTabsProvider = ({ children }: { children: ReactNode }) => {
  const [tabs, setTabs] = useState<ChatTabState[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const addTab = useCallback((filters?: Partial<ChatFilters>, name = 'New Tab') => {
    const newTab: ChatTabState = { 
      id: uuidv4(), 
      name,
      filters: { ...getDefaultFilters(), ...filters },
      hits: [],
      totalHits: 0,
      aggregations: { clusters: [], messageTypes: [] },
      highlightedUsers: {},
      isLoading: true,
    };
    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabId(newTab.id);
  }, []);

  const addContextTab = useCallback((anchor: ChatContextAnchor, name = 'Context') => {
    const newTab: ChatTabState = {
      id: uuidv4(),
      name,
      filters: getDefaultFilters(),
      hits: [],
      totalHits: 0,
      aggregations: { clusters: [], messageTypes: [] },
      highlightedUsers: {},
      isLoading: false,
      context: {
        anchor,
        beforeHits: [],
        afterHits: [],
        hasMoreBefore: false,
        hasMoreAfter: false,
        initialLoaded: false,
      },
    };
    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabId(newTab.id);
  }, []);

  const removeTab = (id: string) => {
    const newTabs = tabs.filter(tab => tab.id !== id);
    setTabs(newTabs);
    if (activeTabId === id) {
      setActiveTabId(newTabs.length > 0 ? newTabs[newTabs.length - 1].id : null);
    }
  };

  const setActiveTab = (id: string) => {
    setActiveTabId(id);
  };

  const updateFilters = useCallback((tabId: string, newFilters: Partial<ChatFilters>) => {
    setTabs(prevTabs => 
      prevTabs.map(tab => 
        tab.id === tabId ? { ...tab, filters: { ...tab.filters, ...newFilters } } : tab
      )
    );
  }, []);
  
  const updateTabState = useCallback((tabId: string, newState: Partial<Omit<ChatTabState, 'id' | 'filters'>>) => {
    setTabs(prevTabs => prevTabs.map(tab =>
      tab.id === tabId ? { ...tab, ...newState } : tab
    ));
  }, []);

  const getActiveTab = () => tabs.find(tab => tab.id === activeTabId);

  const updateTabName = (id: string, name: string) => {
    setTabs(tabs.map(tab => tab.id === id ? { ...tab, name } : tab));
  };

  const HIGHLIGHT_COLORS = [
    'rgba(255, 255, 0, 0.1)',   // Yellow
    'rgba(0, 255, 255, 0.1)',   // Cyan
    'rgba(255, 0, 255, 0.1)',   // Magenta
    'rgba(0, 255, 0, 0.1)',     // Green
    'rgba(255, 165, 0, 0.1)',   // Orange
    'rgba(255, 192, 203, 0.1)', // Pink
    'rgba(75, 0, 130, 0.1)',    // Indigo
    'rgba(238, 130, 238, 0.1)', // Violet
    'rgba(255, 0, 0, 0.1)',     // Red
    'rgba(0, 0, 255, 0.1)',     // Blue
  ];

  const toggleUserHighlight = (tabId: string, username: string) => {
    setTabs(tabs.map(tab => {
      if (tab.id === tabId) {
        const newHighlights = { ...tab.highlightedUsers };
        if (newHighlights[username]) {
          delete newHighlights[username];
        } else {
          const usedColors = Object.values(newHighlights);
          const nextColor = HIGHLIGHT_COLORS.find(c => !usedColors.includes(c)) || HIGHLIGHT_COLORS[0];
          newHighlights[username] = nextColor;
        }
        return { ...tab, highlightedUsers: newHighlights };
      }
      return tab;
    }));
  };

  const value = {
    tabs,
    activeTabId,
    addTab,
    addContextTab,
    removeTab,
    setActiveTab,
    updateFilters,
    updateTabState,
    getActiveTab,
    updateTabName,
    toggleUserHighlight,
  };

  return (
    <ChatTabsContext.Provider value={value}>
      {children}
    </ChatTabsContext.Provider>
  );
};
